/**
 * 轻量读取 JPEG EXIF GPS（DJI 航片常见），不依赖 exifr。
 * @param {ArrayBuffer|ArrayBufferView} buf
 * @returns {{ lon: number, lat: number } | null}
 */

/** 有效 WGS84；禁止 Number(null)===0 误当成非洲 Null Island */
export function isValidWgs84Gps(lon, lat) {
  if (lon == null || lat == null || lon === '' || lat === '') return false;
  const lonN = Number(lon);
  const latN = Number(lat);
  if (!Number.isFinite(lonN) || !Number.isFinite(latN)) return false;
  if (Math.abs(latN) > 90 || Math.abs(lonN) > 180) return false;
  if (Math.abs(lonN) < 1e-8 && Math.abs(latN) < 1e-8) return false;
  return true;
}

export function readJpegExifGps(buf) {
  try {
    const u8 = buf instanceof ArrayBuffer ? new Uint8Array(buf) : new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength);
    if (u8.length < 12 || u8[0] !== 0xff || u8[1] !== 0xd8) return null;

    let offset = 2;
    while (offset + 4 < u8.length) {
      if (u8[offset] !== 0xff) break;
      const marker = u8[offset + 1];
      const size = (u8[offset + 2] << 8) | u8[offset + 3];
      if (marker === 0xe1) {
        const start = offset + 4;
        const end = Math.min(u8.length, offset + 2 + size);
        const gps = parseExifGpsSegment(u8.subarray(start, end));
        if (gps) return gps;
      }
      if (marker === 0xda) break; // SOS
      offset += 2 + size;
    }
  } catch (_) {
    /* ignore */
  }
  return null;
}

/** 只读文件头前 256KB，够大多数 EXIF */
export async function readFileExifGps(file) {
  if (!file) return null;
  const slice = file.slice(0, Math.min(file.size, 256 * 1024));
  const buf = await slice.arrayBuffer();
  return readJpegExifGps(buf);
}

function parseExifGpsSegment(seg) {
  // "Exif\0\0"
  if (seg.length < 14) return null;
  if (!(seg[0] === 0x45 && seg[1] === 0x78 && seg[2] === 0x69 && seg[3] === 0x66)) return null;
  const tiff = seg.subarray(6);
  const le = tiff[0] === 0x49 && tiff[1] === 0x49;
  const be = tiff[0] === 0x4d && tiff[1] === 0x4d;
  if (!le && !be) return null;
  const view = new DataView(tiff.buffer, tiff.byteOffset, tiff.byteLength);
  const u16 = (o) => (le ? view.getUint16(o, true) : view.getUint16(o, false));
  const u32 = (o) => (le ? view.getUint32(o, true) : view.getUint32(o, false));

  const ifd0 = u32(4);
  if (ifd0 + 2 > tiff.length) return null;
  const gpsOffset = findTagOffset(view, ifd0, 0x8825, le, u16, u32); // GPSInfo
  if (gpsOffset == null) return null;

  const latRef = readGpsAscii(view, gpsOffset, 0x0001, le, u16, u32); // N/S
  const lat = readGpsRational(view, gpsOffset, 0x0002, le, u16, u32);
  const lonRef = readGpsAscii(view, gpsOffset, 0x0003, le, u16, u32); // E/W
  const lon = readGpsRational(view, gpsOffset, 0x0004, le, u16, u32);
  if (lat == null || lon == null) return null;
  let latN = lat;
  let lonN = lon;
  if (latRef === 'S') latN = -latN;
  if (lonRef === 'W') lonN = -lonN;
  if (!isValidWgs84Gps(lonN, latN)) return null;
  return { lon: lonN, lat: latN };
}

function findTagOffset(view, ifd, tag, le, u16, u32) {
  if (ifd + 2 > view.byteLength) return null;
  const count = u16(ifd);
  for (let i = 0; i < count; i++) {
    const entry = ifd + 2 + i * 12;
    if (entry + 12 > view.byteLength) break;
    if (u16(entry) === tag) {
      const type = u16(entry + 2);
      const valueOffset = entry + 8;
      if (type === 4) return u32(valueOffset); // LONG
      if (type === 3) return u16(valueOffset);
    }
  }
  return null;
}

function findTag(view, ifd, tag, le, u16, u32) {
  if (ifd + 2 > view.byteLength) return null;
  const count = u16(ifd);
  for (let i = 0; i < count; i++) {
    const entry = ifd + 2 + i * 12;
    if (entry + 12 > view.byteLength) break;
    if (u16(entry) !== tag) continue;
    return {
      type: u16(entry + 2),
      count: u32(entry + 4),
      valueOffset: entry + 8,
      entry
    };
  }
  return null;
}

function readGpsAscii(view, ifd, tag, le, u16, u32) {
  const t = findTag(view, ifd, tag, le, u16, u32);
  if (!t) return null;
  // ASCII often inline in 4 bytes
  const b0 = view.getUint8(t.valueOffset);
  return String.fromCharCode(b0);
}

function readGpsRational(view, ifd, tag, le, u16, u32) {
  const t = findTag(view, ifd, tag, le, u16, u32);
  if (!t || t.type !== 5 || t.count < 3) return null;
  const off = u32(t.valueOffset);
  if (off + 24 > view.byteLength) return null;
  const rationals = [];
  for (let i = 0; i < 3; i++) {
    const num = u32(off + i * 8);
    const den = u32(off + i * 8 + 4) || 1;
    rationals.push(num / den);
  }
  return rationals[0] + rationals[1] / 60 + rationals[2] / 3600;
}
